import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TbAlertTriangle, TbArrowLeft, TbUser } from "react-icons/tb";
import intl from "react-intl-universal";
import {
  convertToCents,
  formatCurrency,
} from "../../../Utils/formatters";
import { resolveCustomerPixOverlayCents } from "../../../Utils/computePixOverlayFee";
import { InputMoney } from "../../Atoms/InputMoney";
import { PasswordConfirm } from "../../Molecules";
import { useToast } from "../../../Hooks/toast";

const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 border-b border-gray-100 py-1.5 last:border-b-0">
    <span className="shrink-0 text-xs text-gray-500">{label}</span>
    <span className="min-w-0 text-right text-sm font-semibold text-brand-dark">{value || "—"}</span>
  </div>
);

export const PixPreview = ({
  isCustomValue,
  previewData,
  finishTransaction,
  cancelOperation,
  isLoading,
  onBack,
  onConfirmClick,
  sourceAccountLabel,
  sourceAccountBalance,
  sourceAccountBalanceCents = null,
  sourceAccountBalanceLoading = false,
  feeQuote = null,
  loteSplit = null,
  className = "app-card mx-auto max-w-xl overflow-hidden",
  hideFooter = false,
  onRegisterPrimaryAction,
  onCanContinueChange,
}) => {
  const [amountValue, setAmountValue] = useState(0);
  const [requirePassword, setRequirePassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const { addToast } = useToast();
  const [document, setDocument] = useState("—");
  const useInlinePasswordStep = Boolean(onConfirmClick);

  useEffect(() => {
    if (previewData?.receiver?.document) {
      const digitsOnly = String(previewData.receiver.document).replace(/\D/g, "");

      if (digitsOnly.length >= 14) {
        setDocument(`••${digitsOnly.slice(2, 16)}••`);
      } else if (digitsOnly.length >= 9) {
        setDocument(`•••${digitsOnly.slice(3, 12)}••`);
      } else {
        setDocument(`•••${digitsOnly}••`);
      }
    }
  }, [previewData]);

  const transferAmountCents = useMemo(() => {
    if (isCustomValue) {
      return convertToCents(amountValue) || 0;
    }
    return Math.trunc(Number(previewData?.amount) || 0);
  }, [isCustomValue, amountValue, previewData?.amount]);

  const overlayCents = useMemo(() => {
    const feePayer =
      feeQuote?.feePayer
      ?? previewData?.fee_payer
      ?? previewData?.feePayer
      ?? "customer";
    const feePercent =
      feeQuote?.feePercent
      ?? previewData?.fee_percent
      ?? previewData?.feePercent
      ?? 0;
    const feeFixed =
      feeQuote?.feeFixed
      ?? previewData?.fee_fixed
      ?? previewData?.feeFixed
      ?? 0;
    const hasSchedule =
      feeQuote != null
      || previewData?.fee_payer != null
      || previewData?.feePayer != null
      || previewData?.fee_percent != null
      || previewData?.fee_fixed != null;

    if (hasSchedule) {
      const fromSchedule = resolveCustomerPixOverlayCents({
        amountCents: transferAmountCents,
        feePayer,
        feePercent,
        feeFixed,
        legs: loteSplit?.legs,
      });
      if (fromSchedule > 0) return fromSchedule;
      if (String(feePayer).toLowerCase() === "instance") return 0;
      return Math.max(
        0,
        Math.trunc(
          Number(
            feeQuote?.taxAmountCents
            ?? previewData?.tax_amount
            ?? previewData?.taxAmount,
          ) || 0,
        ),
      );
    }

    if (String(feePayer).toLowerCase() === "instance") return 0;
    return Math.trunc(Number(previewData?.tax_amount ?? previewData?.taxAmount) || 0);
  }, [feeQuote, previewData, transferAmountCents, loteSplit]);

  const totalDebitCents = transferAmountCents + overlayCents;

  const insufficientWithFee =
    !sourceAccountBalanceLoading
    && sourceAccountBalanceCents != null
    && transferAmountCents > 0
    && totalDebitCents > sourceAccountBalanceCents;

  const canContinue =
    transferAmountCents > 0
    && !insufficientWithFee
    && !isLoading;

  useEffect(() => {
    onCanContinueChange?.(canContinue);
  }, [canContinue, onCanContinueChange]);

  useEffect(() => {
    return () => onCanContinueChange?.(false);
  }, [onCanContinueChange]);

  const handleConfirmPixPayment = useCallback(
    async (pass) => {
      if (amountValue <= 0 && previewData.amount <= 0) {
        addToast({
          title: "Adicione um valor!",
          type: "error",
          description: "Não se esqueça de adicionar um valor à transação.",
        });
        return;
      }

      if (insufficientWithFee) return;

      setPasswordError(null);

      const result = await finishTransaction({
        validation: { password_combination: pass },
        amount: isCustomValue ? convertToCents(amountValue) : previewData.amount,
        pix_id: previewData?.pix_id,
        documentToValidate: previewData.documentToValidate,
      });

      if (result?.success) {
        setRequirePassword(false);
        setPasswordError(null);
        return;
      }

      if (result) {
        setPasswordError({
          title: result.title,
          description: result.description,
        });
      }
    },
    [
      previewData,
      amountValue,
      isCustomValue,
      finishTransaction,
      addToast,
      insufficientWithFee,
    ]
  );

  const handleProceedToConfirm = useCallback(() => {
    if (amountValue <= 0 && previewData.amount <= 0) {
      addToast({
        title: "Adicione um valor!",
        type: "error",
        description: "Não se esqueça de adicionar um valor à transação.",
      });
      return;
    }

    if (insufficientWithFee) return;

    if (useInlinePasswordStep) {
      onConfirmClick({
        amount: isCustomValue ? convertToCents(amountValue) : previewData.amount,
      });
      return;
    }

    setRequirePassword(true);
  }, [
    amountValue,
    previewData,
    isCustomValue,
    addToast,
    useInlinePasswordStep,
    onConfirmClick,
    insufficientWithFee,
  ]);

  useEffect(() => {
    if (!hideFooter || !useInlinePasswordStep) return;
    onRegisterPrimaryAction?.(handleProceedToConfirm);
  }, [hideFooter, useInlinePasswordStep, handleProceedToConfirm, onRegisterPrimaryAction]);

  const feeScheduleKnown =
    feeQuote != null
    || previewData?.fee_payer != null
    || previewData?.feePayer != null
    || previewData?.tax_amount != null
    || previewData?.taxAmount != null;
  const showFeeBreakdown = transferAmountCents > 0 && (overlayCents > 0 || feeScheduleKnown);

  return (
    <>
      <div className={className}>
        <div className="border-b border-gray-100 bg-brand-cream/40 px-5 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Valor da transferência
          </p>
          {isCustomValue ? (
            <div className="mt-1.5">
              <InputMoney
                hasInput={false}
                refreshValue={(value) => setAmountValue(value)}
              />
            </div>
          ) : (
            <p className="text-xl font-bold text-brand-dark">
              {formatCurrency(previewData?.amount)}
            </p>
          )}
        </div>

        <div className="px-5 py-3">
          <div className="mb-2.5 flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-brand-cream/60">
              <TbUser className="h-4 w-4 text-brand-dark" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] text-gray-500">Favorecido</p>
              <p className="truncate text-sm font-bold text-brand-dark">
                {previewData?.receiver?.name}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-100 bg-gray-50/50 px-3">
            {sourceAccountLabel ? (
              <DetailRow label="Conta de origem" value={sourceAccountLabel} />
            ) : null}
            <DetailRow label="CPF/CNPJ" value={document} />
            <DetailRow label="Banco" value={previewData?.receiver?.bank_name} />
            <DetailRow label="Data" value={moment().format("DD/MM/YYYY")} />
            {showFeeBreakdown ? (
              <>
                <DetailRow
                  label={intl.get("pix.transfer.feeLabel")}
                  value={
                    overlayCents > 0
                      ? formatCurrency(overlayCents)
                      : intl.get("pix.transfer.noFee")
                  }
                />
                {overlayCents > 0 ? (
                  <DetailRow
                    label={intl.get("pix.transfer.totalDebitLabel")}
                    value={formatCurrency(totalDebitCents)}
                  />
                ) : null}
              </>
            ) : null}
          </div>

          {insufficientWithFee ? (
            <div
              className="mt-3 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5"
              role="alert"
            >
              <TbAlertTriangle
                className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  {intl.get("pix.transfer.insufficientBalanceTitle")}
                </p>
                <p className="mt-0.5 text-sm text-amber-800">
                  {intl.get("pix.transfer.insufficientBalanceWithFee", {
                    available: sourceAccountBalance,
                    total: formatCurrency(totalDebitCents),
                  })}
                </p>
              </div>
            </div>
          ) : null}

          {loteSplit?.count > 1 ? (
            <div
              className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5"
              role="status"
            >
              <p className="text-sm font-semibold text-amber-900">
                Serão {loteSplit.count} Pix
                {loteSplit.residualCents > 0 ? " (último residual)" : ""}.
              </p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-amber-800">
                O valor excede o teto de {formatCurrency(loteSplit.capCents)} por Pix. Confirmando,
                o lote será enviado em {loteSplit.count} transferências para o mesmo destino.
                {loteSplit.residualCents > 0
                  ? ` A última será de ${formatCurrency(loteSplit.residualCents)}.`
                  : ` Cada Pix será de ${formatCurrency(loteSplit.capCents)}.`}
              </p>
            </div>
          ) : null}

          <p className="mt-2.5 text-[11px] leading-relaxed text-gray-400">
            Revise os dados antes de confirmar. A transferência será processada após a validação da senha.
          </p>
        </div>

        {!hideFooter && (
          <div className="flex flex-col-reverse gap-2 border-t border-gray-100 px-5 py-3 sm:flex-row sm:justify-between">
            <button
              type="button"
              className="btn-back w-full sm:w-auto"
              onClick={onBack || cancelOperation}
              disabled={isLoading}
            >
              <TbArrowLeft className="h-4 w-4" />
              Voltar
            </button>
            <button
              type="button"
              className="btn-primary w-full sm:w-auto"
              disabled={!canContinue}
              onClick={handleProceedToConfirm}
            >
              {isLoading ? "Processando..." : "Confirmar transferência"}
            </button>
          </div>
        )}
      </div>

      {!useInlinePasswordStep && (
        <PasswordConfirm
          submitPassword={(e) => handleConfirmPixPayment(e)}
          open={requirePassword}
          onClose={() => {
            setRequirePassword(false);
            setPasswordError(null);
          }}
          error={passwordError}
          loading={isLoading}
          onDismissError={() => setPasswordError(null)}
          stepUpOptions={{
            transactionId: previewData?.pix_id,
            amount: isCustomValue ? convertToCents(amountValue) : previewData?.amount,
          }}
        />
      )}
    </>
  );
};
